import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { StudioPage, Metric, Badge, DataTable, Loading, cx } from "./StudioShared";
import { Mail, Users, UserCheck, UserX, Download, Archive, ArchiveRestore, Trash2, Loader2 } from "lucide-react";

const fmtDate = (s) => (s ? new Date(s).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function Subscribers() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [view, setView] = useState("active");
  const [busyEmail, setBusyEmail] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDelOpen, setBulkDelOpen] = useState(false);

  const load = useCallback(async () => {
    setData(null);
    try { const r = await api.get(`/admin/newsletter?view=${view}${q ? `&q=${encodeURIComponent(q)}` : ""}`); setData(r.data); }
    catch { setData({ total: 0, active: 0, unsubscribed: 0, archived_count: 0, subscribers: [] }); }
  }, [q, view]);
  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { setSelected(new Set()); }, [view, q]);

  const rows = data?.subscribers || [];
  const toggleSel = (email) => setSelected((s) => { const n = new Set(s); n.has(email) ? n.delete(email) : n.add(email); return n; });
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.email));
  const selAllToggle = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.email)));
  const bulkAction = async (action) => {
    const emails = [...selected]; if (!emails.length) return;
    setBulkBusy(true);
    try {
      const { data: res } = await api.post("/admin/newsletter/bulk", { emails, action });
      toast.success(`${action === "delete" ? "Deleted" : action === "archive" ? "Archived" : "Restored"} ${res.affected} subscriber${res.affected === 1 ? "" : "s"}`);
      setSelected(new Set()); setBulkDelOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBulkBusy(false); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/newsletter/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "subscribers.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { /* noop */ }
  };

  const doArchive = async (row, undo = false) => {
    setBusyEmail(row.email);
    try {
      await api.post("/admin/newsletter/archive", { email: row.email, undo });
      toast.success(undo ? "Subscriber restored" : "Subscriber archived");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusyEmail(null); }
  };
  const doDelete = async (row) => {
    setBusyEmail(row.email);
    try {
      await api.post("/admin/newsletter/delete", { email: row.email });
      toast.success("Subscriber deleted");
      setConfirmDel(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusyEmail(null); }
  };

  return (
    <StudioPage
      title="Newsletter Subscribers"
      question="Who's opted in for ATF WebWorks product updates, AI insights, and launch news?"
      testid="subscribers-page"
      actions={
        <button onClick={exportCsv} data-testid="subscribers-export"
          className="flex items-center gap-1.5 border border-gold/40 text-gold hover:bg-gold/10 px-3 py-1.5 text-xs rounded-sm">
          <Download size={13} /> Export CSV
        </button>
      }
    >
      {!data ? <Loading label="Loading subscribers…" /> : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="subscribers-metrics">
            <Metric label="Total Subscribers" value={data.total} icon={Users} testid="metric-total-subs" />
            <Metric label="Active" value={data.active} icon={UserCheck} testid="metric-active-subs" />
            <Metric label="Unsubscribed" value={data.unsubscribed} icon={UserX} testid="metric-unsub" />
            <Metric label="Archived" value={data.archived_count} icon={Archive} testid="metric-archived-subs" />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-1 bg-navy-800 border border-white/10 p-1 rounded-sm" data-testid="subscribers-view">
              <button onClick={() => setView("active")} data-testid="subscribers-view-active"
                className={cx("px-3 py-1.5 text-xs font-medium rounded-sm transition-colors", view === "active" ? "bg-gold text-navy" : "text-slate-400 hover:text-white")}>Active ({data.total})</button>
              <button onClick={() => setView("archived")} data-testid="subscribers-view-archived"
                className={cx("px-3 py-1.5 text-xs font-medium rounded-sm transition-colors", view === "archived" ? "bg-gold text-navy" : "text-slate-400 hover:text-white")}>Archived ({data.archived_count})</button>
            </div>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email…"
              data-testid="subscribers-search"
              className="flex-1 min-w-[200px] sm:w-80 bg-navy-800 border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none" />
          </div>

          <div className="flex items-center gap-3 flex-wrap" data-testid="subscribers-bulk-toolbar">
            <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
              <input type="checkbox" className="accent-gold" checked={allSelected} onChange={selAllToggle} data-testid="subscribers-select-all" /> Select all ({rows.length})
            </label>
            {selected.size > 0 && (
              <div className="flex items-center gap-2" data-testid="subscribers-bulk-bar">
                <span className="text-xs text-gold">{selected.size} selected</span>
                {view === "archived"
                  ? <button onClick={() => bulkAction("unarchive")} disabled={bulkBusy} data-testid="subscribers-bulk-unarchive" className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 rounded-sm disabled:opacity-50"><ArchiveRestore size={13} /> Restore</button>
                  : <button onClick={() => bulkAction("archive")} disabled={bulkBusy} data-testid="subscribers-bulk-archive" className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-gold/40 text-gold hover:bg-gold/10 rounded-sm disabled:opacity-50"><Archive size={13} /> Archive</button>}
                <button onClick={() => setBulkDelOpen(true)} disabled={bulkBusy} data-testid="subscribers-bulk-delete" className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded-sm disabled:opacity-50"><Trash2 size={13} /> Delete</button>
                <button onClick={() => setSelected(new Set())} className="text-xs text-slate-500 hover:text-white">Clear</button>
              </div>
            )}
          </div>

          <DataTable
            testid="subscribers-table"
            empty={view === "archived" ? "No archived subscribers." : "No subscribers yet. The homepage newsletter form feeds this list."}
            columns={[
              { key: "sel", label: "", render: (r) => <input type="checkbox" className="accent-gold" checked={selected.has(r.email)} onChange={() => toggleSel(r.email)} data-testid={`subscriber-select-${r.email}`} /> },
              { key: "email", label: "Email", render: (r) => <span className="text-white flex items-center gap-2"><Mail size={13} className="text-gold" /> {r.email}</span> },
              { key: "created_at", label: "Subscribed", render: (r) => <span className="text-slate-300">{fmtDate(r.created_at)}</span> },
              { key: "source", label: "Source", render: (r) => <span className="text-slate-500 text-xs">{r.source || "—"}</span> },
              { key: "status", label: "Status", render: (r) => <Badge tone={(r.status || "active") === "active" ? "emerald" : "slate"}>{r.status || "active"}</Badge> },
              { key: "actions", label: "", render: (r) => (
                <div className="flex items-center justify-end gap-2">
                  {r.lifecycle_status === "archived" ? (
                    <button onClick={() => doArchive(r, true)} disabled={busyEmail === r.email} data-testid={`subscriber-unarchive-${r.email}`} title="Restore" className="p-1.5 text-slate-400 hover:text-emerald-400 disabled:opacity-40">{busyEmail === r.email ? <Loader2 size={15} className="animate-spin" /> : <ArchiveRestore size={15} />}</button>
                  ) : (
                    <button onClick={() => doArchive(r)} disabled={busyEmail === r.email} data-testid={`subscriber-archive-${r.email}`} title="Archive" className="p-1.5 text-slate-400 hover:text-gold disabled:opacity-40">{busyEmail === r.email ? <Loader2 size={15} className="animate-spin" /> : <Archive size={15} />}</button>
                  )}
                  <button onClick={() => setConfirmDel(r)} disabled={busyEmail === r.email} data-testid={`subscriber-delete-${r.email}`} title="Delete" className="p-1.5 text-slate-400 hover:text-red-400 disabled:opacity-40"><Trash2 size={15} /></button>
                </div>
              ) },
            ]}
            rows={data.subscribers}
            rowTestid={(r) => `subscriber-row-${r.email}`}
          />
        </div>
      )}

      {bulkDelOpen && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setBulkDelOpen(false)} data-testid="subscribers-bulk-confirm">
          <div className="bg-navy border border-red-500/30 w-full max-w-sm p-6 space-y-4 rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-red-300 font-medium"><Trash2 size={18} /> Delete {selected.size} subscriber{selected.size === 1 ? "" : "s"}?</div>
            <p className="text-slate-400 text-sm">Permanently remove {selected.size} selected subscriber{selected.size === 1 ? "" : "s"}. This can't be undone. Records one summarized audit event.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setBulkDelOpen(false)} data-testid="subscribers-bulk-confirm-cancel" className="px-3 py-2 text-sm border border-white/15 text-slate-300 hover:text-white rounded-sm">Cancel</button>
              <button onClick={() => bulkAction("delete")} disabled={bulkBusy} data-testid="subscribers-bulk-confirm-yes" className="px-3 py-2 text-sm bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50 rounded-sm">Delete permanently</button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setConfirmDel(null)} data-testid="subscriber-confirm-delete">
          <div className="bg-navy border border-red-500/30 w-full max-w-sm p-6 space-y-4 rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-red-300 font-medium"><Trash2 size={18} /> Delete this subscriber?</div>
            <p className="text-slate-400 text-sm">Permanently remove <b className="text-white">{confirmDel.email}</b> from your list. This can't be undone. To hide it reversibly instead, use Archive.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDel(null)} data-testid="subscriber-confirm-cancel" className="px-3 py-2 text-sm border border-white/15 text-slate-300 hover:text-white rounded-sm">Cancel</button>
              <button onClick={() => doDelete(confirmDel)} disabled={busyEmail === confirmDel.email} data-testid="subscriber-confirm-yes" className="px-3 py-2 text-sm bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50 rounded-sm">Delete permanently</button>
            </div>
          </div>
        </div>
      )}
    </StudioPage>
  );
}
